/**
 * CSAT Survey Cooldown Utility
 *
 * Manages a per-survey cooldown using localStorage so that users
 * are not shown the same satisfaction survey more than once per day,
 * regardless of how many qualifying actions they perform.
 */

const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Build the localStorage key for a given survey.
 */
function storageKey(surveyId: string): string {
  return `csat_last_shown_${surveyId}`;
}

/**
 * Returns `true` if the cooldown period has elapsed (or the survey
 * has never been shown), meaning it is safe to display the survey.
 */
export function canShowCSATSurvey(surveyId: string): boolean {
  try {
    const raw = localStorage.getItem(storageKey(surveyId));
    if (!raw) return true;
    const lastShown = Number(raw);
    if (Number.isNaN(lastShown)) return true;
    return Date.now() - lastShown >= COOLDOWN_MS;
  } catch {
    // localStorage may be unavailable (e.g. private browsing).
    // Fall back to allowing the survey so we don't silently lose all data.
    return true;
  }
}

/**
 * Record that the survey was just shown so the cooldown starts now.
 */
export function markCSATSurveyShown(surveyId: string): void {
  try {
    localStorage.setItem(storageKey(surveyId), String(Date.now()));
  } catch {
    // Silently ignore — worst case the user sees the survey again sooner.
  }
}
